require('./index.scss')
import PropTypes from 'prop-types'
import { useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'

import CartItemDuration from '@/components/CartItemDuration'
import Expiration from '@/components/CartItemExpiration/'
import DigitalPurchase from '@/components/DigitalPurchase'
import SalesRightsWarning from '@/components/SalesRightsWarning'
import { PAGE_LEVEL_MESSAGE_TYPES } from '@/constants/notificationTypes'
import { actions as flashActions } from '@/reducers/flashMessages'
import {
  hasFulfillmentErrorStatusSelector,
  isBulkOrBulkIntegrationEnabledSelector
} from '@/selectors'
import priceFactory from '@/utils/factory/price/priceFactory'
import variantFactory from '@/utils/factory/variant/variantFactory'

import PageLevelMessage from '../PageLevelMessage'
import DurationEditing from './DurationEditing'
import ImageLink from './ImageLink'
import ProductInfo from './ProductInfo'
import PromoCodeInfo from './PromoCodeInfo'
import Quantity from './Quantity'

const CART_ITEM_VARIANT = 'CartItemVariant'
const cssClass = [
  'd-md-flex',
  'flex-row',
  'align-items-baseline',
  'flex-wrap'
].join(' ')

const ForCart = ({ id }) => {
  const dispatch = useDispatch()
  const isPromoCodeEnabled = useSelector(
    state => state.checkout.isPromoCodeEnabled
  )
  const lineItem = useSelector(state => state.lineItems.items[id])
  const lineItemFulfillmentStatus = lineItem.fulfillmentStatus
  const {
    asset,
    isDiscountEligible,
    quantity,
    variant
  } = lineItem
  const {
    edition,
    daysLeftInLicense,
    hasCurrentRegionWarning,
    isOnlineResource,
    isSubscription,
    productImageUrl,
    slug,
    subtitle,
    title
  } = asset
  const hasFulfillmentErrorStatus = useSelector(
    state => hasFulfillmentErrorStatusSelector(state, lineItem.id)
  )
  const hasNZTax = useSelector(state => state.taxes.hasNZTax)
  const studentPrice = variant.studentPrice
  const isBulkIntegrationEnabled = useSelector(
    state => state.featureFlags.bulkIntegrationEnabled
  )
  const isBulkOrBulkIntegrationEnabled = useSelector(
    isBulkOrBulkIntegrationEnabledSelector
  )

  const price = priceFactory.createPrice(
    'StudentPrice', { hasNZTax, studentPrice }
  ).formattedAmount

  const effectiveQuantity = isBulkOrBulkIntegrationEnabled ? quantity : 1

  const totalPrice = priceFactory.createPrice(
    'StudentPrice', { hasNZTax, quantity: effectiveQuantity, studentPrice }
  ).formattedAmount

  const cartItemVariant = variantFactory.createVariant(
    CART_ITEM_VARIANT, {
    daysLeftInLicense,
    isOnlineResource,
    isSubscription,
    variant
  })

  useEffect(() => {
    flashActions.clearFlashMessages()

    if (hasFulfillmentErrorStatus) {
      dispatch(
        flashActions.setFlashMessages([
          {
            text: lineItemFulfillmentStatus,
            type: PAGE_LEVEL_MESSAGE_TYPES.error,
          },
        ]),
      )
    }
  })

  return (
    <div>
      <If condition={hasFulfillmentErrorStatus}>
        <div className='u-push--bottom line-item-error-message'>
          <PageLevelMessage />
        </div>
      </If>
      <div
        className='flex-row d-flex justify-content-between u-gap'
        id={'cart-item-' + id}
      >
        <div className='d-md-flex flex-row'>
          <div>
            <ImageLink productImageUrl={productImageUrl} slug={slug} />
          </div>
          <div>
            <section aria-labelledby={'product-details-header-' + id}>
              <ProductInfo
                edition={edition}
                id={id}
                slug={slug}
                subtitle={subtitle}
                title={title}
              />
              <div className='item-details'>
                <div className={cssClass}>
                  <CartItemDuration
                    duration={variant.duration}
                    isOnlineResource={isOnlineResource}
                    isSubscription={isSubscription}
                  />
                  <Expiration
                    cartItemVariant={cartItemVariant}
                    isDisplayDivider
                  />
                </div>

                <div className={cssClass}>
                  <Quantity quantity={quantity} />
                  <If condition={isBulkIntegrationEnabled}>
                    <span
                      aria-hidden='true'
                      className='d-none d-md-block mx-md-2'
                    >
                      {'|'}
                    </span>
                    <p>
                      <FormattedMessage id='carts.item_price' />{': '}
                      {price}
                    </p>
                  </If>
                </div>

                <If condition={hasCurrentRegionWarning}>
                  <SalesRightsWarning />
                </If>

                <DigitalPurchase
                  bookshelfAccessDuration={
                    variant.bookshelfAccessDurationHyphenated
                  }
                  duration={variant.duration}
                />

                <If condition={isPromoCodeEnabled}>
                  <PromoCodeInfo isDiscountEligible={isDiscountEligible} />
                </If>

              </div>
            </section>
          </div>
        </div>
        <div className='item-price u-align--right'>
          <div className='u-weight--medium qa-item-price'>
            {totalPrice}
          </div>
          <DurationEditing id={id} />
        </div>
      </div>
    </div>
  )
}

ForCart.propTypes = { id: PropTypes.number.isRequired }

export default ForCart
